import { useEffect, useState } from 'react';
import { getHighlightPermissions, getPermissionColor, permissionKeyToJsPath, roleNameToJsPath } from '@repo/utils';

/** Props for the PermissionHighlightOverlay dev/admin visualization wrapper. */
export interface PermissionHighlightOverlayProps {
  /** Permission keys required to access the wrapped content. */
  permissions?: string[];
  /** Role names required to access the wrapped content. */
  roles?: string[];
  /** App key required to access the wrapped content. */
  app?: string;
  /** Whether the current user actually passes the permission/role check. */
  passes?: boolean;
  children: React.ReactNode;
  /** Render the wrapper as an inline `span` instead of a block `div`. */
  inline?: boolean;
}

/**
 * Dev/admin visualization overlay — when `highlightPermissions` is enabled via
 * `getHighlightPermissions()`, wraps children in a dashed, color-coded border with a
 * badge showing the gating permission/role key and whether the current user
 * passes the check. No-op passthrough when highlighting is off.
 */
export function PermissionHighlightOverlay({
  permissions,
  roles,
  app,
  passes,
  children,
  inline,
}: PermissionHighlightOverlayProps) {
  const [highlightPermissions, setHighlightPermissions] = useState(getHighlightPermissions);

  useEffect(() => {
    const handleHighlightChange = () => setHighlightPermissions(getHighlightPermissions());
    window.addEventListener('prism_highlight_permissions_changed', handleHighlightChange);
    return () => window.removeEventListener('prism_highlight_permissions_changed', handleHighlightChange);
  }, []);

  if (!highlightPermissions) {
    return <>{children}</>;
  }

  const primaryKey = permissions?.[0] ?? roles?.[0] ?? (app ? `App:${app}` : 'Permission');
  const color = getPermissionColor(primaryKey);
  const jsPath = permissionKeyToJsPath.get(primaryKey) ?? roleNameToJsPath.get(primaryKey) ?? (app ? `Apps.${app}` : undefined);

  const Wrapper = inline ? 'span' : 'div';
  const ContentWrapper = inline ? 'span' : 'div';
  const tooltipText = `Permission: ${primaryKey}\nConstant: ${jsPath || 'N/A'}\nStatus: ${passes ? 'Granted' : 'Denied'}`;

  return (
    <Wrapper
      title={tooltipText}
      style={{
        outline: `2px dashed ${color}`,
        outlineOffset: 0,
        borderRadius: 4,
        position: 'relative',
        boxSizing: 'border-box',
        ...(inline ? {} : { display: 'block' }),
      }}
    >
        <span
          style={{
            position: 'absolute',
            top: -10,
            left: 4,
            fontSize: 10,
            lineHeight: 1,
            background: color,
            color: '#fff',
            borderRadius: 3,
            padding: '1px 4px',
            zIndex: 10,
            whiteSpace: 'nowrap',
          }}
        >
          {primaryKey} {passes ? '✅' : '❌'}
        </span>
        <ContentWrapper
          style={!passes ? { opacity: 0.5, filter: 'grayscale(80%)', pointerEvents: 'none' } : undefined}
        >
          {children}
        </ContentWrapper>
    </Wrapper>
  );
}
